import { DownloadIcon } from '@app/components/icons/download';
import { ImageGalleryIcon } from '@app/components/icons/image-gallery';
import { Plus } from '@app/components/icons/plus';
import { ShareWithArrowIcon } from '@app/components/icons/share-with-arrow';
import { useFlag } from '@app/feature-flags/use-flag';
import {
  selectInitialPublishedEventsCount,
  selectPublishToEventsAssignmentsStatus,
} from '@app/routes/content/selectors';
import { hasEventEnded } from '@app/routes/organization/routes/events/utils';
import {
  isFree,
  isFreeTrial,
} from '@app/routes/organization/routes/manage/routes/billing/utils';
import { selectOrganizationPaymentPlanName } from '@app/routes/organization/selectors';
import { getPathParams } from '@app/routes/utils';
import { getUserExternalId } from '@app/store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import {
  FLAG_ENABLE_CREATOR_LAB_DISCOVERY,
  FLAG_ENABLE_STANDALONE_STUDIO,
  FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION,
  FLAG_POST_EVENT_REHEARSAL_BACKUP_RECORDING_EDIT,
} from '@app/utilities/flags';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecordingAnalytics } from '../../hooks';
import { RecordingDownloadDialog } from '../../routes/editor/components/recording-download-dialog';
import { selectRecording, selectRecordingEventInfo } from '../../selector';
import { Switch } from '../switch';

export type ShareActionsHandle = {
  focusPublishTrigger: () => void;
};

export const ShareActions = forwardRef<
  ShareActionsHandle,
  {
    onClose: () => void;
    onPublishToEvent: () => void;
  }
>(function ShareActions({ onClose, onPublishToEvent }, ref) {
  const publishTriggerRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focusPublishTrigger: () => {
        publishTriggerRef.current?.focus();
      },
    }),
    [],
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const eventInfo = useAppSelector(selectRecordingEventInfo);
  const userId = useAppSelector(getUserExternalId);
  const paymentPlanName = useAppSelector(selectOrganizationPaymentPlanName);
  const { org_id, recordingId } = getPathParams(['org_id', 'recordingId']);
  const { trackRecordingEvent } = useRecordingAnalytics();
  const isCreatorLabDiscoveryEnabled = useFlag(
    FLAG_ENABLE_CREATOR_LAB_DISCOVERY,
  );
  const isStandaloneStudioEnabled = useFlag(FLAG_ENABLE_STANDALONE_STUDIO);

  const isStandaloneStudioRecording =
    isStandaloneStudioEnabled &&
    recording?.category === 'main' &&
    recording?.event_id === null;
  const isRehearsalBackupEnabled = useFlag(
    FLAG_POST_EVENT_REHEARSAL_BACKUP_RECORDING_EDIT,
  );

  const canUsePublishToEvents =
    Boolean(paymentPlanName) &&
    !isFree(paymentPlanName) &&
    !isFreeTrial(paymentPlanName);

  const publishedEventsCount = useAppSelector(
    selectInitialPublishedEventsCount,
  );
  const assignmentsStatus = useAppSelector(
    selectPublishToEventsAssignmentsStatus,
  );
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

  const isContentComposerEnabled = useFlag(
    FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION,
  );

  const shouldShowDownloadDialog = useCallback(() => {
    if (!isCreatorLabDiscoveryEnabled || isContentComposerEnabled) return false;
    if (!userId || !recordingId) return false;

    const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
    const storageKey = `download-dialog-shown-${userId}-${recordingId}`;
    const lastShown = localStorage.getItem(storageKey);

    return !lastShown || Date.now() - parseInt(lastShown, 10) > WEEK_IN_MS;
  }, [
    isCreatorLabDiscoveryEnabled,
    isContentComposerEnabled,
    userId,
    recordingId,
  ]);

  const proceedWithDownload = useCallback(() => {
    if (recordingId) {
      dispatch({
        type: '[recording] log download click',
        payload: {
          recordingId,
        },
      });
    }

    if (recording?.download_url) {
      window.open(recording.download_url, '_blank');
    }
  }, [recording, dispatch, recordingId]);

  const saveDialogTimestamp = useCallback(() => {
    if (userId && recordingId) {
      const storageKey = `download-dialog-shown-${userId}-${recordingId}`;
      localStorage.setItem(storageKey, Date.now().toString());
    }
  }, [userId, recordingId]);

  const onDownloadClick = useCallback(() => {
    trackRecordingEvent('ch_recording_editor_share_download_clicked');

    // Check if we should show dialog (with early returns)
    if (shouldShowDownloadDialog()) {
      setIsDownloadDialogOpen(true);
      return;
    }

    // Otherwise download directly
    proceedWithDownload();
    onClose();
  }, [
    trackRecordingEvent,
    shouldShowDownloadDialog,
    proceedWithDownload,
    onClose,
  ]);

  const handleDownloadFromDialog = useCallback(() => {
    saveDialogTimestamp();
    setIsDownloadDialogOpen(false);
    proceedWithDownload();
    onClose();
  }, [saveDialogTimestamp, proceedWithDownload, onClose]);

  const handleGoToCreatorLab = useCallback(() => {
    saveDialogTimestamp();
    setIsDownloadDialogOpen(false);
    // Navigate with source in location state (for page view tracking)
    navigate(
      `/org/${org_id}/content/recordings/${recordingId}/lab/social-post`,
      { state: { source: 'download_dialog' } },
    );
  }, [saveDialogTimestamp, navigate, org_id, recordingId]);

  const onContentHubClick = useCallback(() => {
    trackRecordingEvent('ch_recording_editor_share_contentHub_clicked');
    navigate(`/org/${org_id}/content/hubs`);
  }, [navigate, org_id, trackRecordingEvent]);

  const onPublishClick = useCallback(() => {
    if (!recordingId || !recording) return;

    trackRecordingEvent('ch_recording_editor_share_publishReplay_clicked');

    dispatch({
      type: '[recording] toggle publish started',
      payload: {
        recordingId,
        published: !recording.published,
      },
    });
  }, [dispatch, recordingId, recording, trackRecordingEvent]);

  const onRetryAssignments = useCallback(() => {
    if (!recordingId) return;

    dispatch({
      type: '[content] publish-to-events assignments requested',
      payload: {
        recordingId,
      },
    });
  }, [dispatch, recordingId]);

  return (
    <>
      <div className="[&>*]:border-t [&>*]:border-gray-600 [&>*:first-child]:border-t-0">
        {(!['backup', 'rehearsal'].includes(recording?.category ?? '') ||
          isRehearsalBackupEnabled) &&
          !isStandaloneStudioRecording && (
            <ReplayMenuSection
              recording={recording}
              eventInfo={eventInfo}
              onPublishClick={onPublishClick}
              onPublishToEvent={onPublishToEvent}
              onRetryAssignments={onRetryAssignments}
              publishTriggerRef={publishTriggerRef}
              assignmentsStatus={assignmentsStatus}
              canUsePublishToEvents={canUsePublishToEvents}
              publishedEventsCount={publishedEventsCount}
            />
          )}

        <ShareMenuItem
          onClick={onContentHubClick}
          data-testid="share-menu-content-hub-button"
        >
          <ImageGalleryIcon className="mr-3 h-4 w-4 text-white" />
          {t('content-recording.add-to-hub')}
          <span className="ml-2 p-1 flex items-center justify-center text-[10px] leading-[10px] bg-spring-primary text-white rounded-full font-bold">
            {t('content-recording.new-tag')}
          </span>
        </ShareMenuItem>

        <ShareMenuItem onClick={onDownloadClick}>
          <DownloadIcon className="mr-3 h-4 w-4 text-white" />
          {t('content-recording.download')}
        </ShareMenuItem>
      </div>

      <RecordingDownloadDialog
        isOpen={isDownloadDialogOpen}
        onDownload={handleDownloadFromDialog}
        onGoToCreatorLab={handleGoToCreatorLab}
      />
    </>
  );
});

const ReplayMenuSection = ({
  eventInfo,
  onPublishClick,
  onPublishToEvent,
  onRetryAssignments,
  publishTriggerRef,
  assignmentsStatus,
  canUsePublishToEvents,
  publishedEventsCount,
  recording,
}: {
  assignmentsStatus: ReturnType<typeof selectPublishToEventsAssignmentsStatus>;
  canUsePublishToEvents: boolean;
  eventInfo: ReturnType<typeof selectRecordingEventInfo>;
  onPublishClick: () => void;
  onPublishToEvent: () => void;
  onRetryAssignments: () => void;
  publishedEventsCount: ReturnType<typeof selectInitialPublishedEventsCount>;
  publishTriggerRef: React.Ref<HTMLButtonElement>;
  recording: ReturnType<typeof selectRecording>;
}) => {
  const { t } = useTranslation();

  const isEventEnded = eventInfo
    ? hasEventEnded({
        expired: eventInfo.expired,
        status: eventInfo.status || '',
        timeEnd: eventInfo.timeEnd,
      })
    : false;

  return (
    <div>
      <div className="flex items-center gap-3 px-4 pt-4 pb-2.5">
        <ShareWithArrowIcon className="w-4 h-4" />
        <span className="text-spring-neutral-b0 text-sm leading-[21px] font-semibold truncate max-w-80 select-none">
          {t('content-recording.show-in-event-replays')}
        </span>
      </div>

      {eventInfo && (
        <>
          <div className="px-4 py-2">
            <div className="flex w-full gap-3">
              <div className="rounded-lg overflow-hidden bg-gray-600 flex-shrink-0 self-center">
                {eventInfo.imageUrl ? (
                  <img
                    src={eventInfo.imageUrl}
                    alt="Event thumbnail"
                    draggable={false}
                    className="w-14 h-8 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-14 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg" />
                )}
              </div>

              <div className="w-full flex flex-col items-start justify-start">
                <div className="inline-flex w-full items-center justify-between">
                  <div
                    className="text-spring-neutral-b0 text-sm leading-[21px] font-semibold truncate justify-self-start max-w-64"
                    title={recording?.event_name}
                  >
                    {recording?.event_name ?? eventInfo?.name}
                  </div>
                  <Switch
                    checked={!!recording?.published}
                    onClick={onPublishClick}
                  />
                </div>

                {isEventEnded && (
                  <span className="px-1 select-none text-xs leading-[17px] font-semibold bg-neutral-b1 text-spring-neutral-b3 rounded mt-1 inline-block max-w-max">
                    {t('content-recording.event-ended')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className={cx(canUsePublishToEvents && 'px-4 py-2')}>
        {canUsePublishToEvents &&
          (assignmentsStatus === 'processing' ? (
            <div
              role="status"
              aria-live="polite"
              aria-busy="true"
              aria-label={t('content-recording.loading-published-events')}
              className={cx(
                'h-[34px] w-64 max-w-[85%] animate-pulse rounded-10 border border-gray-600 bg-gray-700',
              )}
            />
          ) : assignmentsStatus === 'failed' ? (
            <div
              role="alert"
              className="flex h-[34px] w-64 max-w-[85%] items-center gap-2 overflow-hidden rounded-10 border border-gray-600 px-3"
            >
              <span
                className="min-w-0 flex-1 truncate select-none text-sm font-medium leading-[21px] text-spring-neutral-b3"
                title={t('content-recording.failed-to-load-published-events')}
              >
                {t('content-recording.failed-to-load-published-events')}
              </span>

              <button
                type="button"
                className={cx(
                  'shrink-0 rounded-10 px-2 py-0.5 text-sm font-medium text-blue-200',
                  'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
                )}
                onClick={onRetryAssignments}
              >
                {t('content-recording.retry')}
              </button>
            </div>
          ) : (
            <button
              ref={publishTriggerRef}
              type="button"
              className={cx(
                'flex items-center cursor-pointer w-fit max-w-[85%]',
                'border border-solid border-gray-600 rounded-10 py-1.5 px-3',
                'text-sm font-bold text-white hover:bg-gray-700 transition-colors',
                'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
              )}
              onClick={onPublishToEvent}
            >
              <Plus className="w-4 h-4 mr-1.5" />

              <span className="truncate">
                {t('content-recording.publish-to-event', {
                  count: publishedEventsCount,
                })}
              </span>
            </button>
          ))}
      </div>
    </div>
  );
};

const ShareMenuItem = ({
  onClick,
  children,
  ...rest
}: {
  onClick: () => void;
  children: React.ReactNode;
} & React.ComponentProps<'button'>) => {
  return (
    <button
      className={cx(
        'group flex w-full items-center px-4 py-2.5 text-sm font-bold text-white',
        'outline-none hover:bg-gray-700 transition-colors focus-visible:bg-gray-700 cursor-pointer',
      )}
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
};
